import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getErrorMessage = (error, fallback = 'Operation failed') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return fallback;
};

const CONDITIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'greater_than_equals', label: 'Equal to and Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'less_than_equals', label: 'Equal to and Less Than' },
  { value: 'between', label: 'Between' },
];

const CONDITION_LABELS = Object.fromEntries(CONDITIONS.map(c => [c.value, c.label]));

const INPUT_TYPES = [
  { value: 'number', label: 'Number' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'time', label: 'Time (HH:MM:SS)' },
];

const formatTimeFromSeconds = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === '') return '';
  const s = parseInt(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
};

const parseTimeToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 3600 + parts[1] * 60;
  return parseInt(timeStr) || 0;
};

const formatValueForType = (val, inputType) => {
  if (inputType === 'time') return formatTimeFromSeconds(val);
  if (inputType === 'percentage') return `${val}%`;
  return String(val);
};

const ValueInput = ({ value, onChange, inputType, className = '', placeholder = '' }) => {
  if (inputType === 'time') {
    const display = typeof value === 'number' ? formatTimeFromSeconds(value) : (value || '');
    return (
      <Input
        className={className}
        value={display}
        placeholder={placeholder || '00:00:00'}
        onChange={(e) => {
          const raw = e.target.value;
          // Allow typing HH:MM:SS format
          const cleaned = raw.replace(/[^0-9:]/g, '');
          // Auto-insert colons
          let formatted = cleaned;
          if (cleaned.length === 2 && !cleaned.includes(':')) formatted = cleaned + ':';
          if (cleaned.length === 5 && cleaned.split(':').length === 2) formatted = cleaned + ':';
          // Parse to seconds on valid format
          if (/^\d{1,2}:\d{2}:\d{2}$/.test(cleaned)) {
            onChange(parseTimeToSeconds(cleaned));
          } else {
            onChange(raw); // keep raw while typing
          }
        }}
        onBlur={(e) => {
          const v = e.target.value;
          if (/^\d{1,2}:\d{2}:\d{2}$/.test(v)) {
            onChange(parseTimeToSeconds(v));
          } else if (/^\d+$/.test(v)) {
            onChange(parseInt(v));
          }
        }}
      />
    );
  }

  return (
    <Input
      type="number"
      step={inputType === 'percentage' ? '0.1' : '1'}
      className={className}
      value={value ?? ''}
      placeholder={placeholder || (inputType === 'percentage' ? '0%' : '0')}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    />
  );
};

const CriterionDisplay = ({ criterion, inputType }) => {
  const cond = CONDITION_LABELS[criterion.condition] || criterion.condition;
  const v1 = formatValueForType(criterion.value, inputType);
  const v2 = criterion.value2 !== null && criterion.value2 !== undefined ? formatValueForType(criterion.value2, inputType) : null;
  const joiner = criterion.logic ? ` [${criterion.logic}]` : '';

  if (criterion.condition === 'between') {
    return <span>{cond} {v1} and {v2} = {criterion.points} pts{joiner}</span>;
  }
  return <span>{cond} {v1} = {criterion.points} pts{joiner}</span>;
};

export default function KPPManagement() {
  const [kppParameters, setKppParameters] = useState([]);
  const [showKPPDialog, setShowKPPDialog] = useState(false);
  const [editingKPP, setEditingKPP] = useState(null);
  const [kppForm, setKppForm] = useState({
    name: '', description: '', max_points: 15, input_type: 'number', scoring_criteria: []
  });
  const [newCriterion, setNewCriterion] = useState({
    condition: 'equals', value: 0, value2: null, points: 0, logic: null
  });

  useEffect(() => { fetchKPPParameters(); }, []);

  const fetchKPPParameters = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/kpp/parameters`, config);
      setKppParameters(response.data);
    } catch (error) {
      console.error('Error fetching KPP parameters:', error);
    }
  };

  const handleToggleKPPStatus = async (kppId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API}/kpp/parameters/${kppId}/toggle-status`, {}, config);
      toast.success(response.data.message);
      fetchKPPParameters();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to toggle status'));
    }
  };

  const handleSeedDefaultKPP = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/kpp/seed-defaults`, {}, config);
      toast.success(response.data.message);
      fetchKPPParameters();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to seed KPP defaults'));
    }
  };

  const resetForm = () => {
    setKppForm({ name: '', description: '', max_points: 15, input_type: 'number', scoring_criteria: [] });
    setNewCriterion({ condition: 'equals', value: 0, value2: null, points: 0, logic: null });
    setEditingKPP(null);
  };

  const handleCreateKPP = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...kppForm, min_score: 0, max_score: 0 };
      if (editingKPP) {
        await axios.put(`${API}/kpp/parameters/${editingKPP.id}`, payload, config);
        toast.success('KPP parameter updated successfully');
      } else {
        await axios.post(`${API}/kpp/parameters`, payload, config);
        toast.success('KPP parameter created successfully');
      }
      setShowKPPDialog(false);
      resetForm();
      fetchKPPParameters();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save KPP parameter'));
    }
  };

  const handleEditKPP = (kpp) => {
    setEditingKPP(kpp);
    setKppForm({
      name: kpp.name, description: kpp.description || '',
      max_points: kpp.max_points || 15, input_type: kpp.input_type || 'number',
      scoring_criteria: kpp.scoring_criteria || []
    });
    setShowKPPDialog(true);
  };

  const addScoringCriterion = () => {
    const criterion = { ...newCriterion };
    if (kppForm.scoring_criteria.length > 0 && !criterion.logic) {
      criterion.logic = 'OR';
    }
    setKppForm({ ...kppForm, scoring_criteria: [...kppForm.scoring_criteria, criterion] });
    setNewCriterion({ condition: 'equals', value: 0, value2: null, points: 0, logic: null });
  };

  const removeScoringCriterion = (index) => {
    const updated = kppForm.scoring_criteria.filter((_, i) => i !== index);
    // Remove logic from first criterion if it becomes first
    if (updated.length > 0 && updated[0].logic) {
      updated[0] = { ...updated[0], logic: null };
    }
    setKppForm({ ...kppForm, scoring_criteria: updated });
  };

  const updateCriterionLogic = (index, logic) => {
    const updated = [...kppForm.scoring_criteria];
    updated[index] = { ...updated[index], logic };
    setKppForm({ ...kppForm, scoring_criteria: updated });
  };

  const handleDeleteKPP = async (kppId) => {
    if (!window.confirm('Are you sure you want to deactivate this KPP parameter?')) return;
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/kpp/parameters/${kppId}`, config);
      toast.success('KPP parameter deactivated');
      fetchKPPParameters();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete KPP parameter'));
    }
  };

  const getInputTypeLabel = (type) => {
    const found = INPUT_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const needsBetween = newCriterion.condition === 'between';

  return (
    <Card data-testid="kpp-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>KPP Parameter Management</CardTitle>
          <CardDescription>Manage Key Performance Parameters for employee evaluations</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeedDefaultKPP} data-testid="seed-kpp-button">
            <RefreshCw className="w-4 h-4 mr-2" /> Seed Defaults
          </Button>
          <Dialog open={showKPPDialog} onOpenChange={(open) => { setShowKPPDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-kpp-button">
                <Plus className="w-4 h-4 mr-2" /> Add KPP
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingKPP ? 'Edit KPP Parameter' : 'Add KPP Parameter'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateKPP} className="space-y-4">
                <div><Label>Name</Label><Input value={kppForm.name} onChange={(e) => setKppForm({...kppForm, name: e.target.value})} required data-testid="kpp-name" /></div>
                <div><Label>Description</Label><Textarea value={kppForm.description} onChange={(e) => setKppForm({...kppForm, description: e.target.value})} data-testid="kpp-description" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Max Points</Label>
                    <Input type="number" value={kppForm.max_points} onChange={(e) => setKppForm({...kppForm, max_points: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Input Type</Label>
                    <Select value={kppForm.input_type} onValueChange={(val) => setKppForm({...kppForm, input_type: val, scoring_criteria: []})}>
                      <SelectTrigger data-testid="kpp-input-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {INPUT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Scoring Criteria */}
                <div>
                  <Label className="text-base font-semibold">Scoring Criteria</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Define rules to map input values to points.
                    {kppForm.input_type === 'time' && ' Values are in HH:MM:SS format.'}
                    {kppForm.input_type === 'percentage' && ' Values are percentages.'}
                  </p>

                  {/* Existing criteria */}
                  {kppForm.scoring_criteria.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {kppForm.scoring_criteria.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 border rounded-lg p-2.5">
                          {i > 0 && (
                            <Select value={c.logic || 'OR'} onValueChange={(val) => updateCriterionLogic(i, val)}>
                              <SelectTrigger className="w-16 h-7 text-xs font-semibold"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="AND">AND</SelectItem>
                                <SelectItem value="OR">OR</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          <span className="flex-1">
                            <CriterionDisplay criterion={c} inputType={kppForm.input_type} />
                          </span>
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => removeScoringCriterion(i)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new criterion */}
                  <div className="border rounded-lg p-3 bg-blue-50/50 space-y-3">
                    <p className="text-xs font-medium text-blue-700">Add Criterion</p>
                    {kppForm.scoring_criteria.length > 0 && (
                      <div>
                        <Label className="text-xs">Logic Connector</Label>
                        <Select value={newCriterion.logic || 'OR'} onValueChange={(val) => setNewCriterion({...newCriterion, logic: val})}>
                          <SelectTrigger className="w-24" data-testid="criterion-logic"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AND">AND</SelectItem>
                            <SelectItem value="OR">OR</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Condition</Label>
                        <Select value={newCriterion.condition} onValueChange={(val) => setNewCriterion({...newCriterion, condition: val})}>
                          <SelectTrigger data-testid="criterion-condition"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Value{kppForm.input_type === 'time' ? ' (HH:MM:SS)' : kppForm.input_type === 'percentage' ? ' (%)' : ''}</Label>
                        <ValueInput
                          value={newCriterion.value}
                          onChange={(val) => setNewCriterion({...newCriterion, value: val})}
                          inputType={kppForm.input_type}
                          className="w-full"
                          data-testid="criterion-value"
                        />
                      </div>
                    </div>
                    {needsBetween && (
                      <div>
                        <Label className="text-xs">Value 2 (upper bound){kppForm.input_type === 'time' ? ' (HH:MM:SS)' : ''}</Label>
                        <ValueInput
                          value={newCriterion.value2}
                          onChange={(val) => setNewCriterion({...newCriterion, value2: val})}
                          inputType={kppForm.input_type}
                          className="w-full"
                        />
                      </div>
                    )}
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <Label className="text-xs">Points Awarded</Label>
                        <Input type="number" value={newCriterion.points} onChange={(e) => setNewCriterion({...newCriterion, points: parseInt(e.target.value) || 0})} data-testid="criterion-points" />
                      </div>
                      <Button type="button" variant="default" size="sm" onClick={addScoringCriterion} className="h-9" data-testid="add-criterion-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="save-kpp-button">{editingKPP ? 'Update KPP' : 'Create KPP'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table data-testid="kpp-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Max Points</TableHead>
              <TableHead>Input Type</TableHead>
              <TableHead>Criteria</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {kppParameters.map((kpp) => (
              <TableRow key={kpp.id} data-testid={`kpp-row-${kpp.id}`}>
                <TableCell className="font-medium">{kpp.name}</TableCell>
                <TableCell className="text-sm text-gray-600 max-w-xs truncate">{kpp.description}</TableCell>
                <TableCell>{kpp.max_points || 15}</TableCell>
                <TableCell><Badge variant="outline">{getInputTypeLabel(kpp.input_type || 'number')}</Badge></TableCell>
                <TableCell>
                  <span className="text-xs text-gray-500">{(kpp.scoring_criteria || []).length} rule{(kpp.scoring_criteria || []).length !== 1 ? 's' : ''}</span>
                </TableCell>
                <TableCell>
                  <Switch checked={kpp.is_active !== false} onCheckedChange={() => handleToggleKPPStatus(kpp.id)} data-testid={`kpp-toggle-${kpp.id}`} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEditKPP(kpp)} data-testid={`edit-kpp-${kpp.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteKPP(kpp.id)} data-testid={`delete-kpp-${kpp.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {kppParameters.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No KPP parameters found. Click "Seed Defaults" to add standard parameters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
